import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Sparkle, Tooth, Briefcase, DotsThree,
    ArrowLeft, ArrowRight, CheckCircle, Calendar,
} from "@phosphor-icons/react";

/**
 * SmileGoalsOnboarding — iter98.40
 *
 * 4-step "tell us about your smile" mini-survey that fires on the
 * patient's first portal visit post-booking. Designed to take ≤30s
 * so first-time patients don't bounce on a long form.
 *
 * Steps (one card at a time, with progress dots + back/skip controls):
 *   1. Primary smile goal — 4 tappable tiles (aesthetics / bite /
 *      confidence / other). Required to count as "completed".
 *   2. What bothers you most about your smile today? — 1-line textarea.
 *   3. Target timeline — 4 chips. If "specific event" → date picker.
 *   4. Previous ortho work? — Yes/No + optional notes.
 *
 * Lifecycle contract:
 *   • Parent passes `open` + `onClose(completed: boolean)`. Parent
 *     mounts this conditionally based on `GET /patient/me/smile-goals`
 *     returning `null` (= not yet completed).
 *   • Skipping ALWAYS calls onClose(false) — answers still get saved
 *     server-side so the doctor sees partial intake, but
 *     `completed_at` only sets when `primary_goal` is present.
 *
 * Doctor-side surfacing: the saved blob lives on `users.smile_goals`
 * and is returned by `GET /api/doctor/patients/{id}` so the Patient
 * Profile page can render a "Patient onboarding intake" section.
 */

const GOAL_TILES = [
    { id: "aesthetics", label: "Aesthetics", subtitle: "Look better in photos & mirror", Icon: Sparkle, color: "from-fuchsia-500 to-pink-500" },
    { id: "functional_bite", label: "Functional bite", subtitle: "Fix how I chew or speak", Icon: Tooth, color: "from-cyan-500 to-blue-500" },
    { id: "confidence", label: "Confidence", subtitle: "For work, dating, or events", Icon: Briefcase, color: "from-amber-500 to-orange-500" },
    { id: "other", label: "Something else", subtitle: "Tell us in your own words", Icon: DotsThree, color: "from-slate-500 to-slate-600" },
];

const TIMELINE_CHIPS = [
    { id: "no_rush", label: "No rush", subtitle: "Whenever's right" },
    { id: "within_6m", label: "Within 6 months", subtitle: "Sooner is better" },
    { id: "within_year", label: "Within a year", subtitle: "Comfortable pace" },
    { id: "specific_event", label: "I have an event", subtitle: "Wedding, graduation, etc." },
];

export default function SmileGoalsOnboarding({ open, onClose }) {
    const [step, setStep] = useState(1);
    const [primaryGoal, setPrimaryGoal] = useState("");
    const [primaryGoalOther, setPrimaryGoalOther] = useState("");
    const [concerns, setConcerns] = useState("");
    const [timeline, setTimeline] = useState("");
    const [timelineEventDate, setTimelineEventDate] = useState("");
    const [priorTreatment, setPriorTreatment] = useState(null);  // null | true | false
    const [priorTreatmentNotes, setPriorTreatmentNotes] = useState("");
    const [saving, setSaving] = useState(false);

    /* Restore in-progress draft from sessionStorage if the user
       refreshed mid-survey. Cleared after a successful save or final
       skip. */
    useEffect(() => {
        if (!open) return undefined;
        try {
            const raw = window.sessionStorage.getItem("invisique_smile_goals_draft");
            if (!raw) return undefined;
            const d = JSON.parse(raw);
            if (d.primary_goal) setPrimaryGoal(d.primary_goal);
            if (d.primary_goal_other) setPrimaryGoalOther(d.primary_goal_other);
            if (d.concerns) setConcerns(d.concerns);
            if (d.timeline) setTimeline(d.timeline);
            if (d.timeline_event_date) setTimelineEventDate(d.timeline_event_date);
            if (d.prior_treatment !== undefined && d.prior_treatment !== null) setPriorTreatment(d.prior_treatment);
            if (d.prior_treatment_notes) setPriorTreatmentNotes(d.prior_treatment_notes);
            if (d.step) setStep(d.step);
        } catch { /* corrupt draft — ignore */ }
        return undefined;
    }, [open]);

    /* Mid-survey persistence — every keystroke / pick gets stashed so a
       refresh doesn't lose 25 seconds of work. */
    useEffect(() => {
        if (!open) return;
        try {
            window.sessionStorage.setItem("invisique_smile_goals_draft", JSON.stringify({
                primary_goal: primaryGoal,
                primary_goal_other: primaryGoalOther,
                concerns,
                timeline,
                timeline_event_date: timelineEventDate,
                prior_treatment: priorTreatment,
                prior_treatment_notes: priorTreatmentNotes,
                step,
            }));
        } catch { /* private mode — no-op */ }
    }, [open, primaryGoal, primaryGoalOther, concerns, timeline, timelineEventDate, priorTreatment, priorTreatmentNotes, step]);

    const clearDraft = () => {
        try { window.sessionStorage.removeItem("invisique_smile_goals_draft"); } catch { /* noop */ }
    };

    const persist = async (final) => {
        const payload = {};
        if (primaryGoal) payload.primary_goal = primaryGoal;
        if (primaryGoalOther) payload.primary_goal_other = primaryGoalOther.trim();
        if (concerns) payload.concerns = concerns.trim();
        if (timeline) payload.timeline = timeline;
        if (timelineEventDate && timeline === "specific_event") payload.timeline_event_date = timelineEventDate;
        if (priorTreatment !== null) payload.prior_treatment = priorTreatment;
        if (priorTreatmentNotes && priorTreatment) payload.prior_treatment_notes = priorTreatmentNotes.trim();
        if (Object.keys(payload).length === 0) return;
        try {
            await api.put("/patient/me/smile-goals", payload);
        } catch (err) {
            // Non-fatal — toast but don't block close. The patient can
            // always re-open the survey from the Profile page.
            toast.error(err.response?.data?.detail || "Could not save your answers right now.");
            return;
        }
        if (final) {
            clearDraft();
            toast.success("Thanks — your doctor will see this before your visit.");
        }
    };

    const handleSkip = async () => {
        setSaving(true);
        await persist(false);  // save whatever they DID fill — partial is still useful
        setSaving(false);
        clearDraft();
        onClose(false);
    };

    const handleFinish = async () => {
        setSaving(true);
        await persist(true);
        setSaving(false);
        onClose(true);
    };

    const canGoNext = () => {
        if (step === 1) return Boolean(primaryGoal);  // primary goal is the only hard gate
        return true;
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleSkip(); }}>
            <DialogContent
                className="sm:max-w-lg p-0 overflow-hidden bg-card"
                data-testid="smile-goals-onboarding"
            >
                {/* iter98.44 — Radix a11y: every DialogContent needs a
                    DialogTitle for screen readers. We don't want a visible
                    one here (the design uses per-step copy as the title),
                    so wrap it in VisuallyHidden. */}
                <VisuallyHidden asChild>
                    <DialogTitle>Tell us about your smile goals</DialogTitle>
                </VisuallyHidden>
                <VisuallyHidden asChild>
                    <DialogDescription>A 30-second 4-step survey so your doctor walks into the first visit prepared.</DialogDescription>
                </VisuallyHidden>

                {/* Progress dots */}
                <div className="flex items-center justify-center gap-1.5 pt-6 pb-2">
                    {[1, 2, 3, 4].map((n) => (
                        <span
                            key={n}
                            className={`h-1.5 rounded-full transition-all ${
                                n === step ? "w-6 bg-primary" : n < step ? "w-1.5 bg-primary/60" : "w-1.5 bg-border"
                            }`}
                        />
                    ))}
                </div>

                <div className="px-6 pb-6">
                    {step === 1 && (
                        <Step1Goal
                            primaryGoal={primaryGoal}
                            setPrimaryGoal={setPrimaryGoal}
                            primaryGoalOther={primaryGoalOther}
                            setPrimaryGoalOther={setPrimaryGoalOther}
                        />
                    )}
                    {step === 2 && (
                        <Step2Concerns concerns={concerns} setConcerns={setConcerns} />
                    )}
                    {step === 3 && (
                        <Step3Timeline
                            timeline={timeline}
                            setTimeline={setTimeline}
                            timelineEventDate={timelineEventDate}
                            setTimelineEventDate={setTimelineEventDate}
                        />
                    )}
                    {step === 4 && (
                        <Step4PriorTreatment
                            priorTreatment={priorTreatment}
                            setPriorTreatment={setPriorTreatment}
                            priorTreatmentNotes={priorTreatmentNotes}
                            setPriorTreatmentNotes={setPriorTreatmentNotes}
                        />
                    )}
                </div>

                <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-secondary/30">
                    <button
                        type="button"
                        onClick={handleSkip}
                        disabled={saving}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        data-testid="smile-goals-skip-btn"
                    >
                        Skip for now
                    </button>
                    <div className="flex items-center gap-2">
                        {step > 1 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setStep((s) => s - 1)}
                                disabled={saving}
                                data-testid="smile-goals-back-btn"
                            >
                                <ArrowLeft size={14} /> Back
                            </Button>
                        )}
                        {step < 4 ? (
                            <Button
                                size="sm"
                                onClick={() => setStep((s) => s + 1)}
                                disabled={!canGoNext() || saving}
                                className="rounded-full"
                                data-testid="smile-goals-next-btn"
                            >
                                Next <ArrowRight size={14} className="ml-1" />
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                onClick={handleFinish}
                                disabled={saving}
                                className="rounded-full"
                                data-testid="smile-goals-done-btn"
                            >
                                {saving ? "Saving…" : <>Done <CheckCircle size={14} weight="fill" className="ml-1" /></>}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Step1Goal({ primaryGoal, setPrimaryGoal, primaryGoalOther, setPrimaryGoalOther }) {
    return (
        <div>
            <div className="overline text-primary mb-1">Question 1 of 4</div>
            <h2 className="font-display text-xl font-bold mb-1">What's your main smile goal?</h2>
            <p className="text-xs text-muted-foreground mb-4">Helps your doctor focus on what matters most to you.</p>
            <div className="grid grid-cols-2 gap-2.5">
                {GOAL_TILES.map(({ id, label, subtitle, Icon, color }) => {
                    const active = primaryGoal === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setPrimaryGoal(id)}
                            className={`relative text-left p-3 rounded-xl border transition-all ${
                                active
                                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                    : "border-border bg-card hover:border-primary/40"
                            }`}
                            data-testid={`smile-goal-tile-${id}`}
                        >
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-2`}>
                                <Icon size={16} weight="bold" className="text-white" />
                            </div>
                            <div className="font-semibold text-sm">{label}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{subtitle}</div>
                        </button>
                    );
                })}
            </div>
            {primaryGoal === "other" && (
                <div className="mt-3">
                    <Input
                        value={primaryGoalOther}
                        onChange={(e) => setPrimaryGoalOther(e.target.value)}
                        placeholder="Tell us in a sentence…"
                        maxLength={200}
                        data-testid="smile-goal-other-input"
                    />
                </div>
            )}
        </div>
    );
}

function Step2Concerns({ concerns, setConcerns }) {
    return (
        <div>
            <div className="overline text-primary mb-1">Question 2 of 4</div>
            <h2 className="font-display text-xl font-bold mb-1">What bothers you most today?</h2>
            <p className="text-xs text-muted-foreground mb-4">Optional — a sentence or two is plenty.</p>
            <Textarea
                value={concerns}
                onChange={(e) => setConcerns(e.target.value)}
                placeholder="e.g. My upper front teeth feel a bit crowded after my braces years ago…"
                maxLength={300}
                rows={4}
                className="resize-none"
                data-testid="smile-concerns-input"
            />
            <div className="text-[10px] text-muted-foreground text-right mt-1">
                {concerns.length} / 300
            </div>
        </div>
    );
}

function Step3Timeline({ timeline, setTimeline, timelineEventDate, setTimelineEventDate }) {
    return (
        <div>
            <div className="overline text-primary mb-1">Question 3 of 4</div>
            <h2 className="font-display text-xl font-bold mb-1">Any timeline in mind?</h2>
            <p className="text-xs text-muted-foreground mb-4">We'll plan your aligners accordingly. You can change this any time.</p>
            <div className="grid grid-cols-2 gap-2.5">
                {TIMELINE_CHIPS.map(({ id, label, subtitle }) => {
                    const active = timeline === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setTimeline(id)}
                            className={`text-left p-3 rounded-xl border transition-all ${
                                active
                                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                    : "border-border bg-card hover:border-primary/40"
                            }`}
                            data-testid={`smile-timeline-chip-${id}`}
                        >
                            <div className="font-semibold text-sm">{label}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</div>
                        </button>
                    );
                })}
            </div>
            {timeline === "specific_event" && (
                <div className="mt-3">
                    <label className="text-xs font-semibold mb-1.5 block flex items-center gap-1">
                        <Calendar size={12} /> When's the event?
                    </label>
                    <Input
                        type="date"
                        value={timelineEventDate}
                        onChange={(e) => setTimelineEventDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                        data-testid="smile-timeline-event-date"
                    />
                </div>
            )}
        </div>
    );
}

function Step4PriorTreatment({ priorTreatment, setPriorTreatment, priorTreatmentNotes, setPriorTreatmentNotes }) {
    return (
        <div>
            <div className="overline text-primary mb-1">Question 4 of 4</div>
            <h2 className="font-display text-xl font-bold mb-1">Have you had braces or aligners before?</h2>
            <p className="text-xs text-muted-foreground mb-4">Knowing your history helps your doctor pre-plan.</p>
            <div className="grid grid-cols-2 gap-2.5">
                {[
                    { value: false, label: "No, this is my first time" },
                    { value: true, label: "Yes, I've had treatment before" },
                ].map((opt) => {
                    const active = priorTreatment === opt.value;
                    return (
                        <button
                            key={String(opt.value)}
                            type="button"
                            onClick={() => setPriorTreatment(opt.value)}
                            className={`p-3 rounded-xl border transition-all text-sm font-semibold ${
                                active
                                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                    : "border-border bg-card hover:border-primary/40"
                            }`}
                            data-testid={`smile-prior-${opt.value ? "yes" : "no"}`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
            {priorTreatment === true && (
                <div className="mt-3">
                    <label className="text-xs font-semibold mb-1.5 block">When &amp; what kind? <span className="text-muted-foreground italic">· optional</span></label>
                    <Textarea
                        value={priorTreatmentNotes}
                        onChange={(e) => setPriorTreatmentNotes(e.target.value)}
                        placeholder="e.g. Metal braces age 14, removed age 17"
                        maxLength={500}
                        rows={2}
                        className="resize-none"
                        data-testid="smile-prior-notes-input"
                    />
                </div>
            )}
        </div>
    );
}
